import { emailRegex, nonAlpha, nonAlphaLastName, noSlashDash } from 'utils/constants';

const expandYear = dateStr => {
    if (!dateStr) return dateStr;
    return dateStr.replace(/^(\d{1,2})\/(\d{1,2})\/(\d{2})$/, (_, m, d, y) => {
        return `${m}/${d}/${parseInt(y) + 2000}`;
    });
};

const parseTime = timeStr => {
    if (!timeStr) return null;
    const match = timeStr.match(/^(\d{1,2}):(\d{2})(?::(\d{2}))?\s*(AM|PM)?$/i);
    if (!match) return null;
    let hours = parseInt(match[1]);
    const minutes = parseInt(match[2]);
    const seconds = parseInt(match[3] || 0);
    const meridiem = match[4]?.toUpperCase();
    if (meridiem === 'AM' && hours === 12) hours = 0;
    if (meridiem === 'PM' && hours !== 12) hours += 12;
    return [hours, minutes, seconds];
};

const formatDate = serialNumberDate => {
    try {
        var serialNumberDateDigitsOnly = serialNumberDate.replace(/\D/g, '');
        if (
            serialNumberDateDigitsOnly.length === 5 &&
            !noSlashDash.test(serialNumberDate)
        ) {
            // serial number format - fall through
        } else {
            return serialNumberDate;
        }
        var startDate = new Date('January 1, 1900');
        startDate.setDate(
            startDate.getDate() + Number.parseInt(serialNumberDateDigitsOnly, 10)
        );
        var year = startDate.getFullYear();
        var month = startDate.getMonth() + 1;
        var day = startDate.getDate();
        return (
            (month < 10 ? '0' + month : month) +
            '/' +
            (day < 10 ? '0' + day : day) +
            '/' +
            year
        );
    } catch (error) {
        console.error('An error occurred:', error);
        return null;
    }
};

const formatFullName = name => {
    const nameFrag = name.split(' ');
    return {
        first_name: nameFrag[0].replace(nonAlpha, ''),
        last_name: nameFrag[nameFrag.length - 1].replace(nonAlphaLastName, '')
    };
};

const resolveDate = (plain, serialField) => {
    if (plain) {
        const expanded = expandYear(plain);
        return !isNaN(new Date(expanded)) ? plain : null;
    }
    if (serialField && serialField.length) {
        const formatted = formatDate(serialField);
        return formatted || null;
    }
    return null;
};

function checkIfReservationValid(reservation) {
    const hasFullName = reservation.GuestName
        ? reservation.GuestName.replace(nonAlpha, '').length !== 0
        : false;
    const hasGuestFirstName = reservation.GuestFirstName
        ? reservation.GuestFirstName.replace(nonAlpha, '').length !== 0
        : false;
    const hasGuestLastName = reservation.GuestLastName
        ? reservation.GuestLastName.replace(nonAlpha, '').length !== 0
        : false;

    const firstName = hasFullName
        ? formatFullName(reservation.GuestName).first_name
        : hasGuestFirstName
        ? reservation.GuestFirstName.replace(nonAlpha, '')
        : reservation.first_name?.replace(nonAlpha, '').length !== 0
        ? reservation.first_name
        : null;

    const lastName = hasFullName
        ? formatFullName(reservation.GuestName).last_name
        : hasGuestLastName
        ? reservation.GuestLastName.replace(nonAlphaLastName, '')
        : reservation.last_name?.replace(nonAlphaLastName, '').length !== 0
        ? reservation.last_name
        : null;

    const checkIn = resolveDate(
        reservation.check_in_date,
        reservation.StartDateGuestStay
    );
    const checkOut = resolveDate(
        reservation.check_out_date,
        reservation.EndDateGuestStay
    );

    const error = {
        name: `${firstName ?? '__________'} ${lastName ?? '__________'}`,
        errors: []
    };

    if (!firstName) error.errors.push('missing first_name');
    if (!lastName) error.errors.push('missing last_name');
    if (!checkIn) error.errors.push('missing or poorly formatted check-in date');
    if (!checkOut) error.errors.push('missing or poorly formatted check-out date');
    return error;
}

export function parseAndValidateReservationsData(data) {
    if (!Array.isArray(data) || data.length === 0) {
        return { validRows: [], invalidRows: [] };
    }

    const dataKeys = Object.keys(data[0]);
    const validRows = [];
    const invalidRows = [];

    for (let i = 0; i < data.length; i++) {
        const resRow = {};
        dataKeys.forEach(key => {
            resRow[key] = data[i][key];
        });
        const validated = checkIfReservationValid(resRow);
        if (validated.errors.length) {
            invalidRows.push({
                ...validated,
                success: false,
                reason: validated.errors.join(', ')
            });
        } else {
            validRows.push(resRow);
        }
    }

    return { validRows, invalidRows };
}

export function buildReservationBody(res, orgName) {
    const rawCheckIn = res.check_in_date || formatDate(res.StartDateGuestStay?.trim());
    const rawCheckOut = res.check_out_date || formatDate(res.EndDateGuestStay?.trim());

    const checkInDateTime = new Date(expandYear(rawCheckIn));
    const checkInTime = parseTime(res.check_in_time);
    if (checkInTime) {
        checkInDateTime.setHours(...checkInTime);
    } else {
        checkInDateTime.setHours(0, 0, 0);
    }

    const checkOutDateTime = new Date(expandYear(rawCheckOut));
    const checkOutTime = parseTime(res.check_out_time);
    if (checkOutTime) {
        checkOutDateTime.setHours(...checkOutTime);
    } else {
        checkOutDateTime.setHours(23, 59, 0);
    }

    const phone =
        res.phone_number && res.phone_code && res.phone_country
            ? {
                  number: Number.parseInt(res.phone_number),
                  code: Number.parseInt(res.phone_code),
                  country: res.phone_country
              }
            : null;

    const phone_number =
        res.phone_number && res.phone_code
            ? `${res.phone_code}${res.phone_number}`
            : null;

    const validEmail =
        res.email && res.email.trim().length
            ? emailRegex.test(res.email)
                ? res.email.toLowerCase()
                : null
            : null;

    const hasFullName = res.GuestName
        ? res.GuestName.replace(nonAlpha, '').length !== 0
        : false;
    const hasGuestFirstName = res.GuestFirstName
        ? res.GuestFirstName.replace(nonAlpha, '').length !== 0
        : false;
    const hasGuestLastName = res.GuestLastName
        ? res.GuestLastName.replace(nonAlpha, '').length !== 0
        : false;

    const guestFirstName = hasFullName
        ? formatFullName(res.GuestName).first_name
        : hasGuestFirstName
        ? res.GuestFirstName.replace(nonAlpha, '')
        : res.first_name?.trim() || null;

    const guestLastName = hasFullName
        ? formatFullName(res.GuestName).last_name
        : hasGuestLastName
        ? res.GuestLastName.replace(nonAlphaLastName, '')
        : res.last_name?.trim() || null;

    return {
        org_name: orgName,
        property_id: null,
        first_name: guestFirstName,
        last_name: guestLastName,
        email: validEmail,
        phone,
        phone_number,
        address: {
            address_1: res.address_1 || null,
            address_2: res.address_2 || null,
            city: null,
            latitude: null,
            longitude: null,
            state: null,
            zip: null
        },
        check_in_date: checkInDateTime.toDateString(),
        check_in_time: checkInTime
            ? {
                  hours: checkInDateTime.getHours(),
                  minutes: checkInDateTime.getMinutes()
              }
            : null,
        check_out_date: checkOutDateTime.toDateString(),
        check_out_time: checkOutTime
            ? {
                  hours: checkOutDateTime.getHours(),
                  minutes: checkOutDateTime.getMinutes()
              }
            : null,
        client_reservation_id: res.client_reservation_id || null
    };
}
